/**
 * Task: CodebaseDownloadCard — single big CTA that streams the full repo zip.
 *
 * Backend endpoint generates the zip on-the-fly with:
 *   - All source files (excluding node_modules, .git, .venv, build artefacts)
 *   - .env.example templates instead of real .env (safe to share)
 *   - Embedded README.md (5-min local setup) and DEPLOY.md (VPS / Docker / PaaS)
 */
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, Package, Server, Cloud, Code, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CodebaseDownloadCard() {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/documentation/codebase.zip`, {
        credentials: 'include',
      });
      if (!res.ok) {
        toast.error('Failed to download codebase');
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `AutoShopFlow_codebase_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Codebase zip downloaded — bundle ships with README + DEPLOY instructions');
    } catch (e) {
      console.error(e);
      toast.error('Download failed — try again');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card
      className="p-6 border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white"
      data-testid="codebase-download-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
          <Code className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-slate-900">Full codebase (zip)</h3>
          <p className="text-sm text-slate-600 mt-1">
            Backend (FastAPI) + Frontend (React) + tests, packaged with{' '}
            <code className="bg-slate-100 px-1 rounded text-xs">README.md</code> and{' '}
            <code className="bg-slate-100 px-1 rounded text-xs">DEPLOY.md</code> at the top.
            Excludes node_modules, .venv, .git and secrets — safe to share.
          </p>

          {/* What's inside */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <div className="p-3 bg-white rounded-lg border border-slate-200" data-testid="codebase-feature-local">
              <Server className="h-4 w-4 text-blue-600 mb-1.5" />
              <p className="text-xs font-semibold text-slate-900">Local setup</p>
              <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                5-minute quickstart — Python venv, MongoDB, yarn install, first SA user seed.
              </p>
            </div>
            <div className="p-3 bg-white rounded-lg border border-slate-200" data-testid="codebase-feature-vps">
              <Server className="h-4 w-4 text-emerald-600 mb-1.5" />
              <p className="text-xs font-semibold text-slate-900">VPS deploy</p>
              <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                Step-by-step for Ubuntu 22.04 / Debian 12 (DigitalOcean, EC2, Hetzner). Nginx + PM2 + free TLS via certbot.
              </p>
            </div>
            <div className="p-3 bg-white rounded-lg border border-slate-200" data-testid="codebase-feature-docker">
              <Cloud className="h-4 w-4 text-violet-600 mb-1.5" />
              <p className="text-xs font-semibold text-slate-900">Docker / PaaS</p>
              <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                docker-compose recipe + Vercel/Railway/Render + MongoDB Atlas instructions.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="flex items-center gap-3 mt-5">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              size="lg"
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="codebase-download-btn"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Bundling…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download codebase zip
                </>
              )}
            </Button>
            <span className="text-xs text-slate-500 inline-flex items-center gap-1">
              <Package className="h-3 w-3" />
              ~45 MB · regenerated on every download
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
